import { OrganizationCountry } from "../enums/country";
import { Locales } from "../enums/locales";
import { LocaleContent, normalizeContentLocale } from "../locales/translations";
const END_OF_YEAR_SEASONAL_VARIANTS = {
    [OrganizationCountry.UnitedStates]: "givingTuesday",
    [OrganizationCountry.Canada]: "givingTuesday",
    [OrganizationCountry.GreatBritain]: "christmas",
    [OrganizationCountry.Ireland]: "christmas",
    [OrganizationCountry.Australia]: "christmas",
    [OrganizationCountry.NewZealand]: "christmas",
    [OrganizationCountry.Germany]: "advent",
};
const END_OF_YEAR_LAST_CHANCE_COUNTRIES = [
    OrganizationCountry.UnitedStates,
    OrganizationCountry.Canada,
    OrganizationCountry.Germany,
];
export const getEndOfYearSeasonalVariant = (country) => END_OF_YEAR_SEASONAL_VARIANTS[country] ?? null;
export const usesGivingTuesdayBanner = (variant, locale) => variant === "givingTuesday" && locale !== Locales.FR;
export const isEndOfYearLastChanceCountry = (country) => END_OF_YEAR_LAST_CHANCE_COUNTRIES.includes(country);
const SHARED_IMPACT_AND_CLOSING = {
    [Locales.EN]: {
        impact: "Your gift helps {orgName} keep our work going and reach even more people in the year ahead. Every donation counts, whatever the amount.",
        closing: ["Want to help all year long? Choose a monthly gift.", "Thank you for being part of our community."],
    },
    [Locales.FR]: {
        impact: "Votre don aide {orgName} à poursuivre son travail et à rejoindre encore plus de personnes au cours de la prochaine année. Chaque don compte, peu importe le montant.",
        closing: ["Envie d'aider toute l'année ? Choisissez un don mensuel.", "Merci de faire partie de notre communauté."],
    },
    [Locales.ES]: {
        impact: "Tu donación ayuda a {orgName} a continuar con nuestra labor y a llegar a aún más personas el próximo año. Cada donación cuenta, sin importar el monto.",
        closing: [
            "¿Quieres ayudar todo el año? Elige una donación mensual.",
            "Gracias por ser parte de nuestra comunidad.",
        ],
    },
    [Locales.DE]: {
        impact: "Ihre Spende hilft {orgName}, unsere Arbeit fortzusetzen und im kommenden Jahr noch mehr Menschen zu erreichen. Jede Spende zählt, unabhängig vom Betrag.",
        closing: [
            "Sie möchten das ganze Jahr über helfen? Wählen Sie eine monatliche Spende.",
            "Vielen Dank, dass Sie Teil unserer Gemeinschaft sind.",
        ],
    },
};
const SEASONAL_DONATION_FORM_COPY = {
    givingTuesday: {
        [Locales.EN]: {
            title: "Giving Tuesday: support {orgName}",
            opening: "This Giving Tuesday, December 1, people around the world come together to support the causes they care about. Today, we're asking you to join them.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.EN],
        },
        [Locales.FR]: {
            title: "Mardi je donne : soutenez {orgName}",
            opening: "Ce Mardi je donne, le 1er décembre, des gens de partout dans le monde se mobilisent pour soutenir les causes qui leur tiennent à cœur. Aujourd'hui, nous vous invitons à vous joindre à eux.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.FR],
        },
        [Locales.ES]: {
            title: "Giving Tuesday: apoya a {orgName}",
            opening: "Este Giving Tuesday, 1 de diciembre, personas de todo el mundo se unen para apoyar las causas que les importan. Hoy te invitamos a unirte a ellas.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.ES],
        },
    },
    christmas: {
        [Locales.EN]: {
            title: "This Christmas, support {orgName}",
            opening: "Christmas is a time when people come together to support the causes they care about. This season, we're asking you to join them.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.EN],
        },
    },
    advent: {
        [Locales.EN]: {
            title: "This Advent, support {orgName}",
            opening: "Advent is a time when people come together to support the causes they care about. This season, we're asking you to join them.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.EN],
        },
        [Locales.DE]: {
            title: "Advent: Unterstützen Sie {orgName}",
            opening: "In der Adventszeit setzen sich Menschen gemeinsam für die Anliegen ein, die ihnen am Herzen liegen. Wir laden Sie ein, dabei zu sein.",
            ...SHARED_IMPACT_AND_CLOSING[Locales.DE],
        },
    },
};
export const getSeasonalDonationFormCopy = (variant, locale) => SEASONAL_DONATION_FORM_COPY[variant][locale] ?? SEASONAL_DONATION_FORM_COPY[variant][Locales.EN];
const LAPSED_DONOR_EMAIL_COPY = {
    [Locales.EN]: {
        subject: "We've missed you this year!",
        previewText: "Your gift last year made a real difference.",
        firstNameLabel: "First name",
        greeting: "Hi {firstName},",
        paragraphs: [
            "Last year, you chose to support {organizationName}, and we haven't forgotten it. Thank you.",
            "Your generosity helped us keep our mission going and be there for the people who count on us.",
            "As the year comes to an end, would you consider renewing your support? Any amount makes a difference, and it means a lot to know you're still by our side.",
        ],
        buttonLabel: "Give again",
        closingParagraphs: [
            "If you've already given this year by check, or another way, please ignore this note, and thank you!",
        ],
        signOff: "With gratitude,",
        postScript: null,
    },
    [Locales.FR]: {
        subject: "Vous nous avez manqué cette année !",
        previewText: "Votre don de l'an dernier a fait une réelle différence.",
        firstNameLabel: "Prénom",
        greeting: "Bonjour {firstName},",
        paragraphs: [
            "L'an dernier, vous avez choisi de soutenir {organizationName}, et nous ne l'avons pas oublié. Merci.",
            "Votre générosité nous a aidés à poursuivre notre mission et à être présents pour les personnes qui comptent sur nous.",
            "Alors que l'année tire à sa fin, accepteriez-vous de renouveler votre soutien ? Chaque montant fait une différence, et c'est précieux de savoir que vous êtes toujours à nos côtés.",
        ],
        buttonLabel: "Donner à nouveau",
        closingParagraphs: ["Si vous avez déjà donné cette année par chèque ou autrement, ignorez ce message, et merci !"],
        signOff: "Avec gratitude,",
        postScript: null,
    },
    [Locales.DE]: {
        subject: "Wir haben Sie dieses Jahr vermisst!",
        previewText: "Ihre Spende im letzten Jahr hat wirklich etwas bewirkt.",
        firstNameLabel: "Vorname",
        greeting: "Hallo {firstName},",
        paragraphs: [
            "Letztes Jahr haben Sie sich entschieden, {organizationName} zu unterstützen, und das haben wir nicht vergessen. Vielen Dank.",
            "Ihre Großzügigkeit hat uns geholfen, unsere Mission fortzuführen und für die Menschen da zu sein, die auf uns zählen.",
            "Zum Jahresende möchten wir Sie fragen: Würden Sie Ihre Unterstützung erneuern? Jeder Betrag hilft, und es bedeutet uns viel zu wissen, dass Sie weiterhin an unserer Seite sind.",
        ],
        buttonLabel: "Erneut spenden",
        closingParagraphs: [
            "Falls Sie dieses Jahr bereits per Scheck oder auf anderem Weg gespendet haben, betrachten Sie diese Nachricht bitte als gegenstandslos. Vielen Dank!",
        ],
        signOff: "Mit herzlichem Dank,",
        postScript: null,
    },
};
export const getLapsedDonorEmailCopy = (locale) => LAPSED_DONOR_EMAIL_COPY[locale] ?? LAPSED_DONOR_EMAIL_COPY[Locales.EN];
const LAST_CHANCE_EMAIL_COPY = {
    [Locales.EN]: {
        subject: "Last chance to give in 2026 & get a tax receipt",
        previewText: "Gifts made by December 31 count toward your 2026 taxes.",
        firstNameLabel: "First name",
        greeting: "Hi {firstName},",
        paragraphs: [
            "As 2026 comes to a close, thank you for being part of {organizationName} this year. Your support helped us move our mission forward.",
            "There are only a few days left to make a gift that counts for 2026. Gifts made by December 31 are eligible for a 2026 tax receipt.",
            "If you're able, a gift before December 31 will help us start 2027 ready to do even more.",
        ],
        buttonLabel: "Give before December 31",
        closingParagraphs: [
            "You'll receive your tax receipt by email right after your donation.",
            "If you've already given this year, thank you! We're so grateful for your support.",
        ],
        signOff: "Wishing you a happy new year,",
        postScript: "P.S. Gifts must be made by 11:59 PM on December 31 to count for 2026.",
    },
    [Locales.FR]: {
        subject: "Dernière chance de donner en 2026 et d'obtenir un reçu fiscal",
        previewText: "Les dons faits d'ici le 31 décembre comptent pour vos impôts de 2026.",
        firstNameLabel: "Prénom",
        greeting: "Bonjour {firstName},",
        paragraphs: [
            "Alors que 2026 tire à sa fin, merci d'avoir fait partie de {organizationName} cette année. Votre soutien nous a aidés à faire avancer notre mission.",
            "Il ne reste que quelques jours pour faire un don qui compte pour 2026. Les dons faits d'ici le 31 décembre donnent droit à un reçu fiscal pour 2026.",
            "Si vous le pouvez, un don avant le 31 décembre nous aidera à commencer 2027 prêts à en faire encore plus.",
        ],
        buttonLabel: "Donner avant le 31 décembre",
        closingParagraphs: [
            "Vous recevrez votre reçu fiscal par courriel tout de suite après votre don.",
            "Si vous avez déjà donné cette année, merci ! Nous vous sommes très reconnaissants de votre soutien.",
        ],
        signOff: "Nous vous souhaitons une bonne année,",
        postScript: "P.-S. Les dons doivent être faits avant 23 h 59 le 31 décembre pour compter pour 2026.",
    },
    [Locales.DE]: {
        subject: "Letzte Chance, 2026 zu spenden und eine Zuwendungsbestätigung zu erhalten",
        previewText: "Spenden bis zum 31. Dezember zählen für Ihre Steuer 2026.",
        firstNameLabel: "Vorname",
        greeting: "Hallo {firstName},",
        paragraphs: [
            "Das Jahr 2026 neigt sich dem Ende zu. Danke, dass Sie dieses Jahr Teil von {organizationName} waren. Ihre Unterstützung hat unsere Mission vorangebracht.",
            "Es bleiben nur noch wenige Tage für eine Spende, die für 2026 zählt. Für Spenden bis zum 31. Dezember erhalten Sie eine Zuwendungsbestätigung für 2026.",
            "Wenn es Ihnen möglich ist, hilft uns eine Spende vor dem 31. Dezember, 2027 mit noch mehr Kraft zu beginnen.",
        ],
        buttonLabel: "Vor dem 31. Dezember spenden",
        closingParagraphs: [
            "Ihre Zuwendungsbestätigung erhalten Sie direkt nach Ihrer Spende per E-Mail.",
            "Falls Sie dieses Jahr bereits gespendet haben: Vielen Dank! Wir sind Ihnen für Ihre Unterstützung sehr dankbar.",
        ],
        signOff: "Wir wünschen Ihnen ein gutes neues Jahr,",
        postScript: "PS: Spenden müssen bis 23:59 Uhr am 31. Dezember eingehen, um für 2026 zu zählen.",
    },
};
const UNITED_STATES_NON_ITEMIZER_NOTE = "New this year: even if you don't itemize, you may be able to deduct up to $1,000 of your donations ($2,000 for married couples filing jointly).";
export const getLastChanceEmailCopy = (locale, country) => {
    const copy = LAST_CHANCE_EMAIL_COPY[locale] ?? LAST_CHANCE_EMAIL_COPY[Locales.EN];
    if (country !== OrganizationCountry.UnitedStates) {
        return copy;
    }
    const [introduction, deadline, ...rest] = copy.paragraphs;
    return { ...copy, paragraphs: [introduction, `${deadline} ${UNITED_STATES_NON_ITEMIZER_NOTE}`, ...rest] };
};
const THANK_YOU_LETTER_COPY = {
    [Locales.EN]: {
        firstNameLabel: "First name",
        greeting: "Dear {firstName},",
        paragraphs: [
            "Thank you for your gift to {organizationName} in 2026.",
            "Because of your generosity, we were able to keep our mission going and be there for the people who count on us this year.",
            "Your support truly made a difference. As we look ahead to 2027, we're excited to keep building on everything we've achieved together.",
            "Thank you for believing in our mission. We wish you and your loved ones a peaceful holiday season and a happy new year.",
        ],
        signOff: "With gratitude,",
    },
    [Locales.FR]: {
        firstNameLabel: "Prénom",
        greeting: "Bonjour {firstName},",
        paragraphs: [
            "Merci pour votre don à {organizationName} en 2026.",
            "Grâce à votre générosité, nous avons pu poursuivre notre mission et être présents cette année pour les personnes qui comptent sur nous.",
            "Votre soutien a vraiment fait une différence. Alors que nous nous tournons vers 2027, nous avons hâte de continuer à bâtir sur tout ce que nous avons accompli ensemble.",
            "Merci de croire en notre mission. Nous vous souhaitons, à vous et à vos proches, de joyeuses fêtes et une bonne année.",
        ],
        signOff: "Avec gratitude,",
    },
    [Locales.DE]: {
        firstNameLabel: "Vorname",
        greeting: "Hallo {firstName},",
        paragraphs: [
            "Vielen Dank für Ihre Spende an {organizationName} im Jahr 2026.",
            "Dank Ihrer Großzügigkeit konnten wir unsere Mission fortführen und in diesem Jahr für die Menschen da sein, die auf uns zählen.",
            "Ihre Unterstützung hat wirklich etwas bewirkt. Mit Blick auf 2027 freuen wir uns darauf, gemeinsam auf allem Erreichten weiter aufzubauen.",
            "Danke, dass Sie an unsere Mission glauben. Wir wünschen Ihnen und Ihren Liebsten besinnliche Feiertage und ein gutes neues Jahr.",
        ],
        signOff: "Mit herzlichem Dank,",
    },
};
export const getThankYouLetterCopy = (locale) => THANK_YOU_LETTER_COPY[locale] ?? THANK_YOU_LETTER_COPY[Locales.EN];
const GERMAN_SEASONAL_THANK_YOU_EMAIL = {
    emailObject: "Vielen Dank für Ihre großartige Unterstützung in dieser Zeit des Gebens!",
    emailContent: '<p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Hallo <span contenteditable="false" data-custom-variable="{{firstname}}" fr-original-class="fr-deletable styled-tag-chip">Vorname</span>,</p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)"><br /></p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Wir sind Ihnen von Herzen dankbar für Ihre großzügige Unterstützung in dieser Zeit des Gebens! Dank Spenderinnen und Spendern wie Ihnen können wir als Gemeinschaft zusammenhalten und in dieser Zeit viel bewirken.</p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)"><br /></p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Ihre Spende macht bereits einen Unterschied und bringt uns unserer Mission näher. Ohne Sie wäre das nicht möglich!</p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)"><br /></p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Jede einzelne Spende zählt, und Ihre Großzügigkeit hilft uns, wichtige Schritte in eine bessere und hoffnungsvollere Zukunft zu gehen. Danke, dass Sie an unser Anliegen glauben und in dieser Zeit des Gebens an unserer Seite stehen.</p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)"><br /></p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Wir halten Sie gerne über die positiven Veränderungen auf dem Laufenden, die Sie möglich gemacht haben!</p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)"><br /></p><p style="width:100%;margin-top:0px;margin-bottom:0px;box-sizing:inherit;color:rgb(0, 0, 0)">Herzliche Grüße,<br />{userName}<br />{orgName}</p>',
};
export const getSeasonalThankYouEmail = (locale) => locale === Locales.DE
    ? GERMAN_SEASONAL_THANK_YOU_EMAIL
    : LocaleContent.GivingTuesdayContent[normalizeContentLocale(locale)];
